import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Database } from "@revelate/types";
import { formatRiskPointChange } from "@/revenue-intelligence/lib/risk-contracts";
import { differenceInCalendarDays, format } from "date-fns";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { SortableDealTable } from "../forecast-experience/DealsByOutcome";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { StateOfSalesV2ForecastMovementDeal } from "../lib/risk";
import { formatAnalysisMoney } from "@/revenue-intelligence/lib/moneyDisplay";
import { AnalysisInfoTooltip } from "./AnalysisNarrative";
import {
  buildForecastMovementRows,
  forecastMovementDomain,
  ForecastMovementDealsByCategory,
  ForecastMovementRow,
  ForecastMovementValues,
  type ForecastMovementAdditionalRow,
} from "./forecastMovement";
import { stateOfSalesV2Colors } from "./visualTokens";

type Currency = Database["public"]["Tables"]["currencies"]["Row"] | null;

const WATERFALL_CATEGORY_GAP_PERCENT = 12;
const WATERFALL_BAR_SHARE = 1 - (WATERFALL_CATEGORY_GAP_PERCENT * 2 / 100);

const movementInfoCopy: Partial<Record<string, string>> = {
  new_deals: "Includes deals that are new to the current Forecast population, including deals created or reopened during the 30-day comparison window. The bar shows only their effect on the selected-period Forecast.",
  closed_won: "Uses the actual close date. The bar shows selected-period won revenue minus any contribution already included in the starting Forecast.",
  closed_lost: "Includes deals closed lost during the comparison window. The bar removes only the contribution previously included in the selected-period Forecast.",
  improved_outlook: "Open deals that remained in the selected period and whose probability- and risk-adjusted Forecast contribution increased. The bar is current contribution minus contribution 30 days ago.",
  weakened_outlook: "Open deals that remained in the selected period and whose probability- and risk-adjusted Forecast contribution decreased. The bar is current contribution minus contribution 30 days ago.",
};

function MovementTooltip({
  active,
  companyCurrency,
  onSelect,
  payload,
}: {
  active?: boolean;
  companyCurrency: Currency;
  onSelect: (row: ForecastMovementRow) => void;
  payload?: Array<{ payload: ForecastMovementRow }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row || row.value === null) return null;
  const value = row.kind === "change" && row.value > 0
    ? `+${formatAnalysisMoney(row.value, companyCurrency)}`
    : formatAnalysisMoney(row.value, companyCurrency);
  const previewDeals = row.deals.slice(0, 3);
  const remainingDeals = row.deals.length - previewDeals.length;

  return (
    <Card
      className="w-72 p-0"
      onMouseDown={(event) => event.stopPropagation()}
      onMouseMove={(event) => event.stopPropagation()}
      onPointerMove={(event) => event.stopPropagation()}
    >
      <CardHeader className="space-y-2 p-4">
        <CardTitle className="text-xs">{row.label}</CardTitle>
        <p className="text-sm font-medium tabular-nums text-[#231942]">{value}</p>
        {row.kind === "change" && (
          <div className="space-y-1 border-t border-gray-200 pt-2">
            {previewDeals.length ? previewDeals.map(item => (
              <p key={item.id} className="truncate text-xs text-gray-600">{item.name}</p>
            )) : (
              <p className="text-xs text-gray-500">No deals in this category</p>
            )}
            {remainingDeals > 0 && (
              <p className="text-[11px] text-gray-500">+{remainingDeals} more</p>
            )}
            {row.deals.length > 0 && (
              <button
                type="button"
                className="pt-1 text-left text-[11px] font-medium text-[#5E548E] hover:text-[#231942] hover:underline focus-visible:outline-none focus-visible:underline"
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onSelect(row);
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(row);
                }}
              >
                View all details
              </button>
            )}
          </div>
        )}
      </CardHeader>
    </Card>
  );
}

function MovementBarShape({
  height = 0,
  onSelect,
  payload,
  width = 0,
  x = 0,
  y = 0,
}: {
  height?: number;
  onSelect: (row: ForecastMovementRow) => void;
  payload?: ForecastMovementRow;
  width?: number;
  x?: number;
  y?: number;
}) {
  if (!payload) return null;
  const interactive = payload.kind === "change";
  const connectorEnd = x + width / WATERFALL_BAR_SHARE;
  const connectorY = payload.kind === "change" && Number(payload.value) < 0
    ? y + height
    : y;
  const showConnector = payload.key !== "current" && payload.kind !== "unavailable";
  const open = () => interactive && onSelect(payload);
  const commonProps = interactive ? {
        "aria-label": `View ${payload.label} details`,
    onClick: open,
    onKeyDown: (event: React.KeyboardEvent<SVGGElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        open();
      }
    },
    role: "button",
    tabIndex: 0,
  } : {};

  if (payload.kind === "change" && payload.value === 0) {
    const dashY = y;
    return (
      <g {...commonProps} className="cursor-pointer outline-none">
        {showConnector && (
          <line
            x1={x + width}
            x2={connectorEnd}
            y1={connectorY}
            y2={connectorY}
            stroke="#CBD0DA"
            strokeDasharray="3 3"
            strokeWidth={1}
          />
        )}
        <rect x={x} y={dashY - 10} width={width} height={20} fill="transparent" />
        <line
          x1={x}
          x2={x + width}
          y1={dashY}
          y2={dashY}
          stroke={stateOfSalesV2Colors.neutral}
          strokeLinecap="butt"
          strokeWidth={1.5}
        />
      </g>
    );
  }

  const radius = Math.min(2, height / 2);
  return (
    <g {...commonProps} className={interactive ? "cursor-pointer outline-none" : undefined}>
      {showConnector && (
        <line
          x1={x + width}
          x2={connectorEnd}
          y1={connectorY}
          y2={connectorY}
          stroke="#CBD0DA"
          strokeDasharray="3 3"
          strokeWidth={1}
        />
      )}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={radius}
        ry={radius}
        fill={payload.color}
      />
      {payload.kind === "total" && height > radius && radius > 0 && (
        <rect
          x={x}
          y={y + height - radius}
          width={width}
          height={radius}
          fill={payload.color}
        />
      )}
    </g>
  );
}

function formatMovementDate(value: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : format(date, "d MMM yyyy");
}

function formatMovementImpact(value: number, companyCurrency: Currency) {
  return value > 0
    ? `+${formatAnalysisMoney(value, companyCurrency)}`
    : formatAnalysisMoney(value, companyCurrency);
}

function formatContributionChange(
  item: StateOfSalesV2ForecastMovementDeal,
  companyCurrency: Currency,
) {
  const previous = item.previousForecastContribution === null
    ? "—"
    : formatAnalysisMoney(item.previousForecastContribution, companyCurrency);
  const current = item.currentForecastContribution === null
    ? "—"
    : formatAnalysisMoney(item.currentForecastContribution, companyCurrency);
  return `${previous} → ${current}`;
}

function formatRiskChange(item: StateOfSalesV2ForecastMovementDeal) {
  return formatRiskPointChange(item.previousRisk, item.currentRisk);
}

function getRelevantMovementChange(
  row: ForecastMovementRow,
  item: StateOfSalesV2ForecastMovementDeal,
) {
  if (row.key === "new_deals") {
    return `Created or reopened ${formatMovementDate(item.createdOrReopenedAt)} · ${item.currentStage ?? "Stage unavailable"}`;
  }
  if (row.key === "closed_won" || row.key === "closed_lost") {
    return `Closed ${formatMovementDate(item.actualCloseDate)} · ${item.includedInStartForecast ? "Included" : "Not included"} in starting Forecast`;
  }
  if (row.key === "moved_in" || row.key === "moved_out") {
    const previous = item.previousExpectedClose ? new Date(item.previousExpectedClose) : null;
    const current = item.currentExpectedClose ? new Date(item.currentExpectedClose) : null;
    const days = previous && current
      && !Number.isNaN(previous.getTime())
      && !Number.isNaN(current.getTime())
      ? differenceInCalendarDays(current, previous)
      : null;
    const direction = days === null || days === 0
      ? ""
      : ` · ${Math.abs(days)} days ${days > 0 ? "later" : "earlier"}`;
    return `${formatMovementDate(item.previousExpectedClose)} → ${formatMovementDate(item.currentExpectedClose)}${direction}`;
  }
  if (row.key === "improved_outlook" || row.key === "weakened_outlook") {
    return item.changeSummary ?? "Forecast outlook changed";
  }
  return item.missingHistoricalInput ?? "Historical state is incomplete";
}

export function SortableMovementDealList({ companyCurrency, row }: { companyCurrency: Currency; row: ForecastMovementRow }) {
  const columns = useMemo<ColumnDef<StateOfSalesV2ForecastMovementDeal>[]>(() => [
    { accessorKey: "name", header: "Deal" },
    { accessorKey: "ownerName", header: "Owner" },
    { accessorKey: "forecastImpact", header: "Forecast impact", sortingFn: "basic", cell: ({ row }) => formatMovementImpact(row.original.forecastImpact, companyCurrency) },
    { id: "currentForecastContribution", accessorFn: item => item.currentForecastContribution ?? undefined, header: "Forecast contribution", sortingFn: "basic", sortUndefined: "last", cell: ({ row }) => formatContributionChange(row.original, companyCurrency) },
    { id: "currentRisk", accessorFn: item => item.currentRisk ?? undefined, header: "Risk", sortingFn: "basic", sortUndefined: "last", cell: ({ row }) => formatRiskChange(row.original) },
    { id: "changedIndicators", header: "Changed indicators", accessorFn: item => item.changedIndicators.map(change => `${change.label}: ${formatRiskPointChange(change.previousRisk, change.currentRisk)}`).join(" · "),
      cell: ({ row }) => row.original.changedIndicators.length ? row.original.changedIndicators.map(change => <span key={change.indicator} className="block">{change.label}: {formatRiskPointChange(change.previousRisk, change.currentRisk)}</span>) : "No supported indicator change is available for this period." },
    { id: "relevantChange", header: "Relevant change", accessorFn: item => getRelevantMovementChange(row, item) },
    { id: "observed", header: "Observed", sortingFn: "basic", accessorFn: item => new Date(item.changeObservedAt ?? item.comparisonPeriodStart).getTime(),
      cell: ({ row }) => row.original.changeObservedAt ? formatMovementDate(row.original.changeObservedAt) : `${formatMovementDate(row.original.comparisonPeriodStart)}–${formatMovementDate(row.original.comparisonPeriodEnd)}` },
  ], [companyCurrency, row]);
  return <div className="max-h-[62vh] overflow-auto rounded-[8px] border border-gray-200"><SortableDealTable rows={row.deals} columns={columns}
    numericColumns={["forecastImpact", "currentForecastContribution", "currentRisk"]} nowrapColumns={["observed"]} className="min-w-[1320px]"
    rowAttributes={item => ({ "data-forecast-impact": item.forecastImpact })} emptyMessage="No deals are included in this category for the selected comparison." /></div>;
}

function MovementDealList({
  companyCurrency,
  row,
}: {
  companyCurrency: Currency;
  row: ForecastMovementRow;
}) {
  const headers = [
    "Deal",
    "Owner",
    "Forecast impact",
    "Forecast contribution",
    "Risk",
    "Changed indicators",
    "Relevant change",
    "Observed",
  ];

  if (!row.deals.length) {
    return (
      <div className="rounded-[8px] border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
        No deals are included in this category for the selected comparison.
      </div>
    );
  }

  return (
    <div className="max-h-[62vh] overflow-auto rounded-[8px] border border-gray-200">
      <table className="w-full min-w-[1320px] border-collapse text-left text-xs">
        <thead className="sticky top-0 z-10 bg-gray-50 text-gray-500">
          <tr>
            {headers.map(header => (
              <th key={header} className="border-b border-gray-200 px-4 py-3 font-medium">{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {row.deals.map(item => (
            <tr key={item.id} data-forecast-impact={item.forecastImpact} className="border-b border-gray-100 last:border-b-0">
              <td className="max-w-64 px-4 py-3 font-medium text-[#231942]">{item.name}</td>
              <td className="px-4 py-3 text-gray-600">{item.ownerName}</td>
              <td className="px-4 py-3 font-medium tabular-nums text-[#231942]">
                {formatMovementImpact(item.forecastImpact, companyCurrency)}
              </td>
              <td className="px-4 py-3 tabular-nums text-gray-600">
                {formatContributionChange(item, companyCurrency)}
              </td>
              <td className="px-4 py-3 tabular-nums text-gray-600">
                {formatRiskChange(item)}
              </td>
              <td className="max-w-64 px-4 py-3 text-gray-600">
                {item.changedIndicators.length ? item.changedIndicators.map(change => (
                  <span key={change.indicator} className="block">
                    {change.label}: {formatRiskPointChange(change.previousRisk, change.currentRisk)}
                  </span>
                )) : (
                  <span className="text-gray-500">No supported indicator change is available for this period.</span>
                )}
              </td>
              <td className="max-w-72 px-4 py-3 text-gray-600">
                {getRelevantMovementChange(row, item)}
              </td>
              <td className="px-4 py-3 tabular-nums text-gray-600">
                {item.changeObservedAt
                  ? formatMovementDate(item.changeObservedAt)
                  : `${formatMovementDate(item.comparisonPeriodStart)}–${formatMovementDate(item.comparisonPeriodEnd)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ForecastMovementWaterfall({
  companyCurrency,
  currentForecast,
  dealsByCategory,
  movementValues,
  onCategorySelect,
  snapshotForecast,
  additionalRows,
  dateLabels,
  infoCopy,
  compact = false,
  sortableDeals = false,
}: {
  companyCurrency: Currency;
  currentForecast: number;
  dealsByCategory?: ForecastMovementDealsByCategory | null;
  movementValues?: ForecastMovementValues | null;
  onCategorySelect?: (key: string) => void;
  snapshotForecast: number;
  additionalRows?: ForecastMovementAdditionalRow[];
  dateLabels?: { start: string; current: string };
  infoCopy?: Partial<Record<string, string>>;
  compact?: boolean;
  sortableDeals?: boolean;
}) {
  const [selectedRow, setSelectedRow] = useState<ForecastMovementRow | null>(null);
  const rows = buildForecastMovementRows(
    snapshotForecast,
    currentForecast,
    movementValues,
    dealsByCategory,
    additionalRows,
  ).filter(row => !compact || row.kind !== "change" || row.value !== 0 || row.deals.length > 0);
  if (dateLabels) {
    rows[0] = { ...rows[0], label: dateLabels.start };
    rows[rows.length - 1] = { ...rows[rows.length - 1], label: dateLabels.current };
  }
  const domain = forecastMovementDomain(rows);
  const selectRow = (row: ForecastMovementRow) => {
    setSelectedRow(row);
    onCategorySelect?.(row.key);
  };

  return (
    <>
      <div className="overflow-x-auto" data-testid="forecast-movement-waterfall" data-start-forecast={snapshotForecast} data-current-forecast={currentForecast}>
        <div className={compact ? "min-w-[640px]" : "min-w-[1120px]"}>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                barCategoryGap={`${WATERFALL_CATEGORY_GAP_PERCENT}%`}
                data={rows}
                margin={{ top: 20, right: 24, bottom: 0, left: 0 }}
              >
                <CartesianGrid vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={false} tickLine={false} axisLine={false} height={0} />
                <YAxis
                  domain={domain}
                  interval={0}
                  tickFormatter={(value) => formatAnalysisMoney(value, companyCurrency)}
                  tick={{ fontSize: 11, fill: "#6B7280", dy: -3 }}
                  tickLine={false}
                  axisLine={false}
                  width={88}
                />
                <ChartTooltip
                  content={(
                    <MovementTooltip
                      companyCurrency={companyCurrency}
                      onSelect={selectRow}
                    />
                  )}
                  wrapperStyle={{ pointerEvents: "auto" }}
                />
                <Bar dataKey="base" stackId="movement" fill="transparent" isAnimationActive={false} />
                <Bar
                  dataKey="barValue"
                  stackId="movement"
                  isAnimationActive={false}
                  shape={<MovementBarShape onSelect={selectRow} />}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div
            className="ml-[88px] mr-[24px] grid border-t border-gray-200 pt-3"
            style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` }}
          >
            {rows.map(row => {
              const value = row.value === null
                ? "—"
                : row.kind === "change" && row.value > 0
                  ? `+${formatAnalysisMoney(row.value, companyCurrency)}`
                  : formatAnalysisMoney(row.value, companyCurrency);
              const infoText = (infoCopy ?? movementInfoCopy)[row.key];

              return (
                <div key={row.key} data-movement-key={row.key} data-movement-value={row.value ?? undefined} className="min-w-0 px-1 text-center">
                  <div className={`flex items-start justify-center ${compact ? "min-h-12" : "min-h-8"}`}>
                    <p className={`${compact ? "break-words " : ""}text-[11px] font-medium leading-4 text-gray-700`}>{row.label}</p>
                  </div>
                  <div className="mt-1 flex min-h-5 items-center justify-center gap-1">
                    <p className="text-xs font-medium tabular-nums text-[#231942]">{value}</p>
                    {infoText && (
                      <AnalysisInfoTooltip compact label={row.label} text={infoText} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={selectedRow !== null} onOpenChange={(open) => !open && setSelectedRow(null)}>
        <DialogContent className="max-h-[85vh] max-w-5xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>{selectedRow?.label ?? "Forecast movement deals"}</DialogTitle>
            <DialogDescription>
              {selectedRow
                ? `${selectedRow.deals.length} ${selectedRow.deals.length === 1 ? "deal" : "deals"} in this movement category, ${sortableDeals ? "initially " : ""}ranked by absolute Forecast impact. Individual deal values are intentionally omitted.`
                : "Deals included in the selected Forecast movement category."}
            </DialogDescription>
          </DialogHeader>
          {selectedRow && (
            sortableDeals ? <SortableMovementDealList companyCurrency={companyCurrency} row={selectedRow} /> : <MovementDealList companyCurrency={companyCurrency} row={selectedRow} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
